<template>
  <div id="illinois-my-bets" class="illinois-my-bets">
    <transition
      mode="out-in"
      enter-active-class="ib-enter-active"
      enter-class="ib-enter"
      enter-to-class="ib-enter-to"
      leave-active-class="ib-leave-active"
      leave-class="ib-leave"
      leave-to-class="ib-leave-to"
    >
      <!-- Loading -->
      <div v-if="loading" key="loading" class="ib-loading">
        <svg class="ib-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="ib-spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="ib-spinner__arc"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Not authenticated -->
      <div v-else-if="!getAuthenticated" key="not-auth" class="ib-empty">
        {{ $t('loginMyBets') }}
      </div>

      <!-- Content -->
      <div v-else key="content">
        <div v-if="myBets.length > 0" class="ib-title">
          <span>{{ $t('lastBets') }}</span>
        </div>

        <div v-if="myBets.length === 0 && !loading" class="ib-empty">
          <svg class="ib-empty__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <div class="ib-empty__text">{{ $t('noMyBets') }}</div>
        </div>

        <div v-if="myBets.length > 0" class="ib-list">
          <BetItem
            v-for="(bet, index) in myBets"
            :key="bet.code + '_' + index"
            :bet="bet"
            @click="openDetail(bet)"
          >
            <template #cashout>
              <!-- betFreeze teaser: only when the server says a leg can be frozen -->
              <div v-if="!cashoutOnly && betFreezeTeaser(bet)" class="ib-freeze-teaser" @click.stop="openDetail(bet)">
                <span class="ib-freeze-teaser__text">&#10052; {{ $t('betFreeze.teaser.text') }}</span>
                <span class="ib-freeze-teaser__cta">{{ $t('betFreeze.teaser.cta') }} &rsaquo;</span>
              </div>
              <div
                v-if="bet.result === 'pending' && isCashoutEligible(bet) && isCashoutVisible(bet)"
                class="ib-cashout-row"
                @click.stop
              >
                <template v-if="!(cashoutConfirmBet && cashoutConfirmBet.code === bet.code)">
                  <button
                    v-if="getCashoutByCode(bet.code).status === 'available'"
                    class="ib-cashout-btn"
                    @click.stop="openCashoutConfirm(bet, getCashoutByCode(bet.code))"
                  >
                    {{ $t('ritira') }} {{ formatCashoutAmount(bet) }} {{ bet.currency }}
                    <span v-if="getCashoutByCode(bet.code).hasActiveLiveEvents" class="ib-cashout-live">LIVE</span>
                  </button>
                  <button
                    v-else-if="['suspended', 'temporary'].includes(getCashoutByCode(bet.code).status)"
                    class="ib-cashout-btn ib-cashout-btn--disabled"
                    disabled
                  >
                    {{ $t('ritira') }}
                  </button>
                  <button
                    v-else-if="getCashoutByCode(bet.code).status === 'loading'"
                    class="ib-cashout-btn ib-cashout-btn--disabled"
                    disabled
                  >
                    <span class="ib-cashout-spin"></span> {{ $t('ritira') }}
                  </button>
                </template>

                <div v-else class="ib-cashout-panel">
                  <template v-if="cashoutInlineStep === 'confirm'">
                    <div v-if="cashoutFrozenExpired" class="ib-cashout-expired">{{ $t('cashout.expired') }}</div>
                    <div v-else class="ib-cashout-confirm-text">
                      {{ $t('cashout.confirmMsg') }}
                      <strong>{{ cashoutAmountForConfirm != null ? cashoutAmountForConfirm.toFixed(2) : '' }} {{ bet.currency }}</strong>
                    </div>
                    <div class="ib-cashout-actions">
                      <button class="ib-cashout-cancel" :disabled="cashoutExecuting" @click.stop="closeCashoutInline">{{ $t('removeBet') }}</button>
                      <button class="ib-cashout-confirm" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting" @click.stop="confirmCashout">
                        {{ cashoutFrozenExpired ? $t('cashout.retry') : $t('Agree') }}
                      </button>
                    </div>
                  </template>
                  <template v-if="cashoutInlineStep === 'double'">
                    <div v-if="cashoutBypassLoading" class="ib-cashout-verifying">{{ $t('cashout.verifying') }}</div>
                    <div v-else-if="cashoutFreshData" class="ib-cashout-confirm-text">
                      {{ $t('cashout.finalConfirmMsg') }}
                      <strong>{{ cashoutFreshData.amount != null ? cashoutFreshData.amount.toFixed(2) : '' }} {{ bet.currency }}</strong>
                    </div>
                    <div class="ib-cashout-actions" v-if="!cashoutBypassLoading && cashoutFreshData">
                      <button class="ib-cashout-cancel" :disabled="cashoutExecuting" @click.stop="closeCashoutInline">{{ $t('removeBet') }}</button>
                      <button class="ib-cashout-confirm" :class="{ 'is-loading': cashoutExecuting }" :disabled="cashoutExecuting" @click.stop="proceedCashout">
                        {{ $t('cashout.proceed') }}
                      </button>
                    </div>
                  </template>
                </div>
              </div>
              <!-- Bet builder: no cashout (server rule), say why instead of a missing button -->
              <div v-if="showBetBuilderCashoutNote(bet)" class="ib-cashout-row ib-cashout-bb-note" @click.stop>
                {{ $t('cashout.betBuilderNotAvailable') }}
              </div>
            </template>
          </BetItem>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BetItem from './BetItem.vue';
import { CASHOUT_TEMPORARY_POLL_INTERVAL_MS, CASHOUT_VISIBLE_STATUSES, CASHOUT_MAX_CONCURRENT, CASHOUT_WORKER_STAGGER_MS, CASHOUT_TASK_GAP_MS } from '@/store/modules/cashout';
import { runWorkerPool, createSleeper } from '@/utils/cashoutWorkerPool';
import betFreezeListMixin from '@/mixins/betFreezeListMixin';

export default {
  name: 'IllinoisMyBets',
  components: { BetItem },
  // betFreeze teaser, shared with the classic MyBets.
  mixins: [betFreezeListMixin],
  props: {
    /**
     * Lists only bets that could still be cashed out. Same contract as
     * MyBets.vue's prop of the same name — set by CartAmerican for its
     * Cashout tab, off for the My Bets tab.
     */
    cashoutOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      cashoutConfirmBet: null,
      cashoutConfirmData: null,
      cashoutExecuting: false,
      cashoutFrozenExpired: false,
      cashoutInlineStep: 'confirm',
      cashoutBypassLoading: false,
      cashoutFreshData: null,
      _cashoutDestroyed: false,
      _cashoutRunActive: false,
      _cashoutPollTimer: null,
      _cashoutTemporaryTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      getAuthenticated: 'user/getAuthenticated',
      getMyBets: 'user/getMyBets',
      getUserType: 'user/getType',
      getCashier: 'user/getCashier',
      getCashoutByCode: 'cashout/getCashoutByCode',
      getCashoutPollIntervalMs: 'settings/getCashoutPollIntervalMs',
      getEnableAgencyCashout: 'settings/getEnableAgencyCashout',
      getEnableCashierCashout: 'settings/getEnableCashierCashout',
      getEnableTerminalCashout: 'settings/getEnableTerminalCashout',
      getProfileType: 'user/getProfileType',
      getTerminal: 'user/getTerminal',
    }),
    myBets() {
      const bets = this.getMyBets || [];
      if (!this.cashoutOnly) return bets;
      return bets.filter(b => b && b.result === 'pending' && this.isCashoutEligible(b));
    },
    showOnlyMyBetsToggle() {
      return (
        (this.getUserType != null && this.getUserType === 4) ||
        (this.getCashier != null && this.getCashier === true)
      );
    },
    cashoutAmountForConfirm() {
      if (!this.cashoutConfirmBet || !this.cashoutConfirmData) return null;
      return this.cashoutConfirmData.amount ?? null;
    },
    isPlayerEligibleForCashout() {
      const profileType = this.getProfileType;
      const isAgency   = this.getUserType != null && this.getUserType == 4;
      const isCashier  = profileType === 'CASHIER' || !!this.getCashier;
      const isTerminal = profileType === 'TERMINAL' || !!this.getTerminal;
      if (isAgency   && this.getEnableAgencyCashout)   return true;
      if (isCashier  && this.getEnableCashierCashout)  return true;
      if (isTerminal && this.getEnableTerminalCashout) return true;
      return !isAgency && !isCashier && !isTerminal;
    },
  },
  async created() {
    this._cashoutDestroyed = false;
    this._cashoutRunActive = false;
    this._cashoutSleeper = createSleeper();
    await this.initialize();
  },
  beforeDestroy() {
    this._cashoutDestroyed = true;
    // Resolves every pending pause at once: workers wake, see the flag, stop.
    if (this._cashoutSleeper) this._cashoutSleeper.cancelAll();
    this._stopCashoutPoller();
    this._stopTemporaryPoller();
  },
  methods: {
    ...mapActions({
      fetchMyBets: 'user/fetchMyBets',
      setDetailMyBet: 'user/setDetailMyBet',
      calculateCashout: 'cashout/calculateCashout',
      executeCashout: 'cashout/executeCashout',
      setSnackbarText: 'settings/setSnackbarText',
      setSnackbar: 'settings/setSnackbar',
    }),
    async initialize() {
      this.loading = true;
      this.$emit('loading-change', true);
      try {
        const payload = { all: false };
        if (this.showOnlyMyBetsToggle) {
          payload.showOnlyMyBets = true;
        }
        await this.fetchMyBets(payload);
      } catch (error) {
        console.error('Error fetching my bets:', error);
      } finally {
        this.loading = false;
        this.$emit('loading-change', false);
        this.$emit('bets-loaded', this.myBets.length > 0);
      }
      await this._triggerCashoutForPendingBets();
      // The Cashout tab does not show the teaser: no availability requests.
      if (!this.cashoutOnly) this._checkBetFreezeForList();
      this._startCashoutPoller();
      this._startTemporaryPoller();
    },

    // ── Cashout ──────────────────────────────────────────────────────────
    isCashoutEligible(bet) {
      // No cashout on a bet with a bet builder (rocky-bets and rocky-definition refuse it too).
      if (this.hasBetBuilderLeg(bet)) return false;
      const INELIGIBLE = ['integral', 'system'];
      // No cashout on a bet with a betFreeze applied (enforced server-side too).
      if (bet.betFreeze === true || (bet.betFreezeCount || 0) > 0) return false;
      return !INELIGIBLE.includes(bet.type);
    },
    // Pending bet builder bet of a player who could otherwise cash out: the server refuses
    // the cashout (BET_BUILDER_CASHOUT_NOT_ALLOWED), so say why instead of a missing button.
    showBetBuilderCashoutNote(bet) {
      return Boolean(bet) && bet.result === 'pending' && this.$store.getters['settings/getCashout'] === true
        && this.isPlayerEligibleForCashout && this.hasBetBuilderLeg(bet);
    },
    hasBetBuilderLeg(bet) {
      return Boolean(bet) && (bet.odds || []).some(odd => odd && odd.is_bet_builder === true);
    },
    isCashoutVisible(bet) {
      return CASHOUT_VISIBLE_STATUSES.has(this.getCashoutByCode(bet.code).status);
    },
    getCashoutAmountForBet(bet) {
      const entry = this.getCashoutByCode(bet.code);
      if (!entry || entry.amount == null) return null;
      return entry.amount;
    },
    formatCashoutAmount(bet) {
      const amount = this.getCashoutAmountForBet(bet);
      return amount != null ? amount.toFixed(2) : '';
    },
    _pendingBets() {
      if (!this.isPlayerEligibleForCashout) return [];
      return (this.getMyBets || []).filter(b => {
        if (b.result !== 'pending') return false;
        if (!this.isCashoutEligible(b)) return false;
        if (this.getCashoutByCode(b.code).status === 'hidden') return false;
        return true;
      });
    },
    _temporaryBets() {
      if (!this.isPlayerEligibleForCashout) return [];
      return (this.getMyBets || []).filter(b =>
        b.result === 'pending' &&
        this.isCashoutEligible(b) &&
        b.code &&
        this.getCashoutByCode(b.code).status === 'temporary',
      );
    },
    async _runCashoutPool(codes) {
      await runWorkerPool(codes, code => this.calculateCashout({ code }), {
        concurrency: CASHOUT_MAX_CONCURRENT,
        staggerMs: CASHOUT_WORKER_STAGGER_MS,
        gapMs: CASHOUT_TASK_GAP_MS,
        isCancelled: () => this._cashoutDestroyed === true,
        sleep: ms => (this._cashoutDestroyed || !this._cashoutSleeper ? Promise.resolve() : this._cashoutSleeper.sleep(ms)),
        // calculateCashout swallows its own HTTP errors, so reaching this means
        // the action itself broke: contained per task, the pool keeps going.
        onError: (code, e) => console.warn('[cashout] pool task failed for', code, e && e.message),
      });
    },
    async _runGuardedCashoutPool(codes) {
      if (this._cashoutDestroyed) return;
      if (this._cashoutRunActive) return;
      this._cashoutRunActive = true;
      try {
        await this._runCashoutPool(codes);
      } finally {
        this._cashoutRunActive = false;
      }
    },
    async _triggerCashoutForPendingBets() {
      const codes = this._pendingBets().map(b => b.code).filter(Boolean);
      await this._runGuardedCashoutPool(codes);
    },
    async _triggerCashoutForTemporaryBets() {
      const codes = this._temporaryBets().map(b => b.code).filter(Boolean);
      await this._runGuardedCashoutPool(codes);
    },
    _startCashoutPoller() {
      this._stopCashoutPoller();
      this._cashoutPollTimer = setInterval(
        () => this._triggerCashoutForPendingBets(),
        this.getCashoutPollIntervalMs,
      );
    },
    _stopCashoutPoller() {
      if (this._cashoutPollTimer) {
        clearInterval(this._cashoutPollTimer);
        this._cashoutPollTimer = null;
      }
    },
    _startTemporaryPoller() {
      this._stopTemporaryPoller();
      this._cashoutTemporaryTimer = setInterval(
        () => this._triggerCashoutForTemporaryBets(),
        CASHOUT_TEMPORARY_POLL_INTERVAL_MS,
      );
    },
    _stopTemporaryPoller() {
      if (this._cashoutTemporaryTimer) {
        clearInterval(this._cashoutTemporaryTimer);
        this._cashoutTemporaryTimer = null;
      }
    },
    openCashoutConfirm(bet, cashoutEntry) {
      if (this.cashoutConfirmBet && this.cashoutConfirmBet.code === bet.code) {
        this.closeCashoutInline();
        return;
      }
      this.cashoutConfirmBet = bet;
      this.cashoutConfirmData = cashoutEntry;
      this.cashoutFrozenExpired = false;
      this.cashoutInlineStep = 'confirm';
    },
    closeCashoutInline() {
      this.cashoutConfirmBet = null;
      this.cashoutConfirmData = null;
      this.cashoutFreshData = null;
      this.cashoutInlineStep = 'confirm';
    },
    async confirmCashout() {
      if (!this.cashoutConfirmBet) return;
      const code = this.cashoutConfirmBet.code;
      this.cashoutFreshData = null;
      this.cashoutBypassLoading = true;
      this.cashoutInlineStep = 'double';
      await this.calculateCashout({ code, bypassCache: true });
      const fresh = this.getCashoutByCode(code);
      this.cashoutBypassLoading = false;
      if (fresh.status === 'available') {
        this.cashoutFreshData = fresh;
      } else {
        this.closeCashoutInline();
      }
    },
    async proceedCashout() {
      if (!this.cashoutConfirmBet) return;
      const code = this.cashoutConfirmBet.code;
      this.cashoutExecuting = true;
      try {
        const result = await this.executeCashout({ code });
        if (result.success) {
          this.closeCashoutInline();
          await this.initialize();
        } else if (result.frozenExpired) {
          this.cashoutFreshData = null;
          this.cashoutBypassLoading = true;
          await this.calculateCashout({ code, bypassCache: true });
          const updated = this.getCashoutByCode(code);
          this.cashoutBypassLoading = false;
          if (updated.status === 'available') {
            this.cashoutFreshData = updated;
          } else {
            this.closeCashoutInline();
          }
        } else {
          this.closeCashoutInline();
          if (result.errorCodes && result.errorCodes.includes('CASHOUT_IS_ONLY_AVAILABLE_FOR_PLAYERS')) {
            this.setSnackbarText(this.$t('cashout.onlyForPlayers'));
            this.setSnackbar(true);
          }
          if (result.errorCodes && result.errorCodes.includes('BET_BUILDER_CASHOUT_NOT_ALLOWED')) {
            this.setSnackbarText(this.$t('cashout.betBuilderNotAvailable'));
            this.setSnackbar(true);
          }
        }
      } finally {
        this.cashoutExecuting = false;
      }
    },
    /**
     * Hands the bet to whoever owns the detail modal — TemplateAmerican, which
     * renders <BetDetailModal> and listens for this event.
     *
     * No live-score enrichment here on purpose. This component has no modal of
     * its own, and TemplateAmerican.openBetDetailModal already fetches the
     * scores for the same bet. Doing it in both fired POST /v3/live-scores
     * TWICE per open, which is what the duplicate request in the network panel
     * was — a duplication that predates the v3 migration (it used to be two
     * legacy loops instead of two batched calls) and only became visible once
     * the requests started succeeding.
     */
    async openDetail(bet) {
      try {
        this.setDetailMyBet(bet);
        this.$root.$emit('openBetDetailModal', bet);
      } catch (error) {
        console.error('Error opening bet detail:', error);
      }
    },

  }
};
</script>

<style scoped>

/* --- Loading --- */
.ib-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  min-height: 200px;
}

.ib-spinner {
  width: 32px;
  height: 32px;
  color: var(--mybets-primary);
  animation: ib-spin 1s linear infinite;
}

.ib-spinner__track {
  opacity: 0.25;
}

.ib-spinner__arc {
  opacity: 0.75;
}

@keyframes ib-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* --- Empty / Not auth --- */
.ib-empty {
  text-align: center;
  padding: 24px 0;
  color: var(--mybets-text-muted);
  font-size: var(--font-size-4xl);
}

.ib-empty__icon {
  width: 40px;
  height: 40px;
  margin: 0 auto 8px;
  opacity: 0.5;
  display: block;
  color: var(--mybets-text-muted);
}

.ib-empty__text {
  font-size: var(--font-size-4xl);
  color: var(--mybets-text-muted);
}

/* --- Title --- */
.ib-title {
  margin-bottom: 12px;
}

.ib-title span {
  font-size: var(--font-size-4xl);
  font-weight: 600;
  color: var(--mybets-text);
}

/* --- List --- */
.ib-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 600px;
  overflow-y: auto;
}

/* --- Cashout --- */
.ib-cashout-bb-note {
  font-size: 12px;
  font-style: italic;
  opacity: 0.75;
}
.ib-cashout-row {
  margin-top: 8px;
}

.ib-cashout-btn {
  width: 100%;
  background: var(--color-primary);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 10px;
  font-size: var(--font-size-4xl);
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: opacity 0.15s ease;
}

.ib-cashout-btn:hover {
  opacity: 0.9;
}

.ib-cashout-btn--disabled {
  background: #00000026;
  color: #00000080;
  cursor: not-allowed;
}

.ib-cashout-live {
  background: #dc2626;
  color: #ffffff;
  font-size: var(--font-size-xs);
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 3px;
  letter-spacing: 0.03em;
}

.ib-cashout-spin {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  animation: ib-spin 0.7s linear infinite;
}

.ib-cashout-panel {
  background: var(--mybets-card-bg);
  border: 1px solid var(--mybets-border);
  border-radius: 6px;
  padding: 10px;
}

.ib-cashout-confirm-text,
.ib-cashout-expired,
.ib-cashout-verifying {
  font-size: var(--font-size-3xl);
  color: var(--mybets-text);
  margin-bottom: 8px;
}

.ib-cashout-confirm-text strong {
  display: block;
  margin-top: 2px;
  font-size: var(--font-size-5xl);
  color: var(--color-primary);
}

.ib-cashout-actions {
  display: flex;
  gap: 8px;
}

.ib-cashout-cancel,
.ib-cashout-confirm {
  flex: 1;
  border: none;
  border-radius: 6px;
  padding: 8px;
  font-size: var(--font-size-3xl);
  font-weight: 700;
  cursor: pointer;
}

.ib-cashout-cancel {
  background: #00000014;
  color: var(--mybets-text);
}

.ib-cashout-confirm {
  background: var(--color-primary);
  color: #ffffff;
}

.ib-cashout-confirm.is-loading,
.ib-cashout-cancel:disabled,
.ib-cashout-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* --- Transitions --- */
.ib-enter-active {
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
}
.ib-leave-active {
  transition: opacity 0.2s ease-in, transform 0.2s ease-in;
}
.ib-enter {
  opacity: 0;
  transform: translateY(16px);
}
.ib-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.ib-leave {
  opacity: 1;
  transform: translateY(0);
}
.ib-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* --- betFreeze teaser: accent #0ea5e9, readable on the light and dark themes --- */
.ib-freeze-teaser {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px dashed rgba(14, 165, 233, 0.55);
  background: rgba(14, 165, 233, 0.08);
  font-size: var(--font-size-2xl);
  cursor: pointer;
  transition: background 0.15s ease;
}

.ib-freeze-teaser:hover {
  background: rgba(14, 165, 233, 0.16);
}

.ib-freeze-teaser__text {
  color: #0ea5e9;
  font-weight: 600;
}

.ib-freeze-teaser__cta {
  color: #0ea5e9;
  font-weight: 700;
  white-space: nowrap;
  margin-left: 8px;
}
</style>
